import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, BookOpen, FileText, Pencil, Plus, Trash2, Eye, EyeOff, Save, ShieldAlert, ToggleRight } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth, authHeaders } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ==========================================================================
// Shell
// ==========================================================================
function AdminShell({ children, active }) {
    return (
        <div className="flex min-h-screen bg-white text-[#0A0A0A]">
            <aside className="hidden w-60 flex-col border-r border-[#E5E5E5] bg-[#FAFAFA] px-4 py-6 lg:flex">
                <div className="px-2">
                    <Logo size="sm" />
                </div>
                <div className="mt-2 px-2 text-[10px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                    Admin
                </div>
                <nav className="mt-6 flex flex-col gap-1">
                    <NavLink to="/admin" active={active === "dashboard"} icon={<BookOpen size={14} strokeWidth={2} />}>
                        Dashboard
                    </NavLink>
                    <NavLink to="/admin/blog" active={active === "blog"} icon={<FileText size={14} strokeWidth={2} />}>
                        Blog Posts
                    </NavLink>
                    <NavLink to="/admin/reports" active={active === "reports"} icon={<ShieldAlert size={14} strokeWidth={2} />}>
                        Share Reports
                    </NavLink>
                    <NavLink to="/admin/flags" active={active === "flags"} icon={<ToggleRight size={14} strokeWidth={2} />}>
                        Feature Flags
                    </NavLink>
                </nav>
                <div className="mt-auto px-2 pb-2 text-[11px] text-[#A3A3A3]">
                    <Link to="/" className="underline-offset-[3px] hover:underline">
                        ← Back to chat
                    </Link>
                </div>
            </aside>
            <main className="flex-1 overflow-x-hidden">
                <div className="border-b border-[#E5E5E5] bg-white px-6 py-4 lg:hidden">
                    <Logo size="sm" />
                </div>
                {children}
            </main>
        </div>
    );
}

function NavLink({ to, active, icon, children }) {
    return (
        <Link
            to={to}
            data-testid={`admin-nav-${to.replace(/\//g, "-")}`}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-[13.5px] transition ${
                active
                    ? "bg-white text-[#0A0A0A] shadow-[inset_0_0_0_1px_#E5E5E5]"
                    : "text-[#525252] hover:bg-white"
            }`}
        >
            {icon}
            {children}
        </Link>
    );
}

function Gate({ children }) {
    const { user, token, loading } = useAuth();
    const navigate = useNavigate();
    useEffect(() => {
        if (loading) return;
        if (!token) navigate("/login?next=/admin");
    }, [loading, token, navigate]);
    if (loading) return <div className="p-10 text-[14px] text-[#737373]">Loading…</div>;
    if (!token) return null;
    if (!user?.is_admin) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6 text-center">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                    Admin
                </div>
                <h1 className="mt-2 text-3xl font-semibold tracking-tight">Not authorised</h1>
                <p className="mt-4 max-w-md text-[14px] text-[#737373]">
                    Your account isn't an admin yet. Ask the site owner to promote you (set
                    <code className="mx-1 rounded bg-[#F5F5F5] px-1 py-0.5 text-[12px]">BOOTSTRAP_ADMIN_EMAIL</code>
                    to your email and restart the backend, or run the
                    <code className="mx-1 rounded bg-[#F5F5F5] px-1 py-0.5 text-[12px]">promote_admin.py</code>
                    helper).
                </p>
                <Link
                    to="/"
                    data-testid="admin-back-to-chat"
                    className="mt-8 inline-flex items-center gap-2 rounded-full border border-[#0A0A0A] bg-white px-4 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:bg-[#0A0A0A] hover:text-white"
                >
                    <ArrowLeft size={13} strokeWidth={2} /> Back to chat
                </Link>
            </div>
        );
    }
    return children;
}

// ==========================================================================
// Dashboard
// ==========================================================================
export function AdminDashboardPage() {
    const { token } = useAuth();
    const [stats, setStats] = useState(null);
    const [overview, setOverview] = useState(null);
    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API}/admin/blog/_stats`, { headers: authHeaders(token) })
            .then((r) => setStats(r.data))
            .catch(() => setStats(null));
        axios
            .get(`${API}/admin/stats`, { headers: authHeaders(token) })
            .then((r) => setOverview(r.data))
            .catch(() => setOverview(null));
    }, [token]);
    return (
        <Gate>
            <AdminShell active="dashboard">
                <div className="mx-auto w-full max-w-[1080px] px-6 py-10" data-testid="admin-dashboard">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                        Admin · Overview
                    </div>
                    <h1 className="mt-2 text-3xl font-semibold tracking-tight">Dashboard</h1>

                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <Stat label="Total users" value={overview?.users ?? "—"} testid="stat-users" />
                        <Stat label="Sessions" value={overview?.sessions ?? "—"} testid="stat-sessions" />
                        <Stat label="Open reports" value={overview?.share_reports_open ?? "—"} testid="stat-reports-open" />
                        <Stat label="Blog posts" value={overview?.blog_posts ?? "—"} testid="stat-blog" />
                    </div>

                    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        <Stat label="Total posts" value={stats?.total ?? "—"} testid="stat-total" />
                        <Stat label="Published" value={stats?.published ?? "—"} testid="stat-published" />
                        <Stat label="Drafts" value={stats?.drafts ?? "—"} testid="stat-drafts" />
                        <Stat label="Total views" value={stats?.total_views ?? "—"} testid="stat-views" />
                        <Stat label="Recent (7d)" value={stats?.recent_updates_7d ?? "—"} testid="stat-recent" />
                    </div>

                    <div className="mt-10 rounded-2xl border border-[#E5E5E5] bg-white p-6">
                        <h3 className="text-[16px] font-semibold tracking-tight">Quick actions</h3>
                        <div className="mt-4 flex flex-wrap gap-3">
                            <Link
                                to="/admin/blog/new"
                                data-testid="admin-new-post"
                                className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#1F1F1F]"
                            >
                                <Plus size={13} strokeWidth={2.25} /> New post
                            </Link>
                            <Link
                                to="/admin/blog"
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-4 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]"
                            >
                                <FileText size={13} strokeWidth={2} /> All posts
                            </Link>
                            <Link
                                to="/admin/reports"
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-4 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]"
                            >
                                <ShieldAlert size={13} strokeWidth={2} /> Review reports
                            </Link>
                            <Link
                                to="/admin/flags"
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-4 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A]"
                            >
                                <ToggleRight size={13} strokeWidth={2} /> Feature flags
                            </Link>
                        </div>
                    </div>
                </div>
            </AdminShell>
        </Gate>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div
            data-testid={testid}
            className="rounded-2xl border border-[#E5E5E5] bg-white px-5 py-5"
        >
            <div className="text-[11px] uppercase tracking-[0.14em] text-[#A3A3A3]">{label}</div>
            <div className="mt-2 text-[28px] font-semibold tracking-tight">{value}</div>
        </div>
    );
}

// ==========================================================================
// Posts list
// ==========================================================================
export function AdminBlogListPage() {
    const { token } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    const reload = () => {
        if (!token) return;
        setLoading(true);
        axios
            .get(`${API}/admin/blog?include_drafts=true&limit=200`, {
                headers: authHeaders(token),
            })
            .then((r) => setPosts(r.data?.posts || []))
            .catch(() => setPosts([]))
            .finally(() => setLoading(false));
    };
    useEffect(reload, [token]);

    const togglePublish = async (post) => {
        await axios.patch(
            `${API}/admin/blog/${post.id}`,
            { published: !post.published },
            { headers: authHeaders(token) },
        );
        reload();
    };
    const remove = async (post) => {
        if (!window.confirm(`Delete "${post.title}"?`)) return;
        await axios.delete(`${API}/admin/blog/${post.id}`, { headers: authHeaders(token) });
        reload();
    };

    return (
        <Gate>
            <AdminShell active="blog">
                <div className="mx-auto w-full max-w-[1200px] px-6 py-10" data-testid="admin-blog-list">
                    <div className="flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                                Admin · Blog
                            </div>
                            <h1 className="mt-2 text-3xl font-semibold tracking-tight">Blog posts</h1>
                        </div>
                        <Link
                            to="/admin/blog/new"
                            data-testid="admin-list-new-btn"
                            className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#1F1F1F]"
                        >
                            <Plus size={13} strokeWidth={2.25} /> New post
                        </Link>
                    </div>

                    <div className="mt-8 overflow-hidden rounded-2xl border border-[#E5E5E5]">
                        <table className="w-full text-left text-[13.5px]">
                            <thead className="border-b border-[#E5E5E5] bg-[#FAFAFA] text-[#737373]">
                                <tr>
                                    <th className="px-4 py-3 font-medium">Title</th>
                                    <th className="px-4 py-3 font-medium">Country</th>
                                    <th className="px-4 py-3 font-medium">Status</th>
                                    <th className="px-4 py-3 font-medium">Views</th>
                                    <th className="px-4 py-3 font-medium">Updated</th>
                                    <th className="px-4 py-3 font-medium" />
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={6} className="px-4 py-10 text-center text-[#A3A3A3]">Loading…</td></tr>
                                )}
                                {!loading && posts.length === 0 && (
                                    <tr><td colSpan={6} className="px-4 py-10 text-center text-[#A3A3A3]">No posts yet.</td></tr>
                                )}
                                {posts.map((p) => (
                                    <tr key={p.id} className="border-b border-[#E5E5E5] last:border-b-0" data-testid={`admin-row-${p.slug}`}>
                                        <td className="px-4 py-3">
                                            <Link
                                                to={`/admin/blog/${p.id}/edit`}
                                                className="font-medium text-[#0A0A0A] underline-offset-[3px] hover:underline"
                                            >
                                                {p.title}
                                            </Link>
                                            <div className="text-[11px] text-[#A3A3A3]">/{p.slug}</div>
                                        </td>
                                        <td className="px-4 py-3 text-[#525252]">
                                            <span className="mr-1.5 align-middle text-[14px]">{p.country_flag}</span>
                                            {p.country_code}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider ${
                                                    p.published
                                                        ? "bg-[#0A0A0A] text-white"
                                                        : "bg-[#F5F5F5] text-[#525252]"
                                                }`}
                                            >
                                                {p.published ? "Published" : "Draft"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-[#525252]">{p.view_count ?? 0}</td>
                                        <td className="px-4 py-3 text-[#737373]">{p.updated_at?.slice(0, 10)}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end gap-1.5">
                                                <Link
                                                    to={`/admin/blog/${p.id}/edit`}
                                                    data-testid={`admin-edit-${p.slug}`}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                                                    title="Edit"
                                                >
                                                    <Pencil size={13} strokeWidth={2} />
                                                </Link>
                                                <button
                                                    type="button"
                                                    onClick={() => togglePublish(p)}
                                                    data-testid={`admin-toggle-${p.slug}`}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#0A0A0A] transition hover:bg-[#F5F5F5]"
                                                    title={p.published ? "Unpublish" : "Publish"}
                                                >
                                                    {p.published ? <EyeOff size={13} strokeWidth={2} /> : <Eye size={13} strokeWidth={2} />}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => remove(p)}
                                                    data-testid={`admin-delete-${p.slug}`}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#9C2A2A] transition hover:bg-[#FBEAEA]"
                                                    title="Delete"
                                                >
                                                    <Trash2 size={13} strokeWidth={2} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </AdminShell>
        </Gate>
    );
}

// ==========================================================================
// Post editor (new / edit)
// ==========================================================================
const COUNTRY_OPTIONS = [
    "IN", "US", "GB", "FR", "IT", "ES", "DE", "AE", "TH", "SG", "MY", "ID",
    "JP", "KR", "VN", "HK", "PH", "TW", "AU", "CA", "MX", "BR", "AR", "GR",
    "PT", "NL", "CH", "TR", "EG", "ZA", "MV", "LK", "NP",
];

function emptyPost() {
    return {
        title: "",
        subtitle: "",
        slug: "",
        country_code: "IN",
        cover_image_url: "",
        body_markdown: "",
        excerpt: "",
        author_name: "",
        tags: [],
        published: false,
        seo: { title: "", description: "", og_image: "" },
    };
}

export function AdminBlogEditPage() {
    const { id } = useParams();
    const isNew = !id;
    const navigate = useNavigate();
    const { token } = useAuth();
    const [draft, setDraft] = useState(emptyPost());
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isNew || !token) return;
        axios
            .get(`${API}/admin/blog/${id}`, { headers: authHeaders(token) })
            .then((r) => setDraft({ ...emptyPost(), ...r.data, seo: r.data.seo || {} }))
            .catch(() => setError("Could not load post"))
            .finally(() => setLoading(false));
    }, [id, isNew, token]);

    const onChange = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
    const onSeoChange = (key, value) =>
        setDraft((d) => ({ ...d, seo: { ...(d.seo || {}), [key]: value } }));

    const submit = async (override = {}) => {
        setSaving(true);
        setError(null);
        const payload = { ...draft, ...override };
        if (typeof payload.tags === "string") {
            payload.tags = payload.tags.split(",").map((t) => t.trim()).filter(Boolean);
        }
        try {
            if (isNew) {
                const r = await axios.post(`${API}/admin/blog`, payload, {
                    headers: authHeaders(token),
                });
                navigate(`/admin/blog/${r.data.id}/edit`, { replace: true });
            } else {
                await axios.patch(`${API}/admin/blog/${id}`, payload, {
                    headers: authHeaders(token),
                });
                setDraft((d) => ({ ...d, ...payload }));
            }
        } catch (e) {
            setError(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!window.confirm(`Delete "${draft.title}"?`)) return;
        await axios.delete(`${API}/admin/blog/${id}`, { headers: authHeaders(token) });
        navigate("/admin/blog");
    };

    const tagsString = useMemo(() => (draft.tags || []).join(", "), [draft.tags]);

    return (
        <Gate>
            <AdminShell active="blog">
                <div
                    className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-6 px-6 py-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
                    data-testid="admin-blog-edit"
                >
                    <div className="flex flex-col gap-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <Link
                                    to="/admin/blog"
                                    className="text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3] hover:underline"
                                >
                                    ← Blog posts
                                </Link>
                                <h1 className="mt-1 text-[26px] font-semibold tracking-tight">
                                    {isNew ? "New post" : "Edit post"}
                                </h1>
                            </div>
                            {!isNew && (
                                <span
                                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider ${
                                        draft.published ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#525252]"
                                    }`}
                                    data-testid="admin-edit-status"
                                >
                                    {draft.published ? "Published" : "Draft"}
                                </span>
                            )}
                        </div>

                        {error && (
                            <div className="rounded-lg border border-[#9C2A2A] bg-[#FBEAEA] px-3 py-2 text-[12.5px] text-[#9C2A2A]">
                                {error}
                            </div>
                        )}

                        {loading ? (
                            <div className="h-96 animate-pulse rounded-2xl bg-[#F5F5F5]" />
                        ) : (
                            <div className="flex flex-col gap-4">
                                <Field label="Title" required>
                                    <input
                                        type="text"
                                        value={draft.title || ""}
                                        onChange={(e) => onChange("title", e.target.value)}
                                        data-testid="admin-field-title"
                                        className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[14.5px] focus:border-[#0A0A0A] focus:outline-none"
                                    />
                                </Field>
                                <Field label="Subtitle">
                                    <input
                                        type="text"
                                        value={draft.subtitle || ""}
                                        onChange={(e) => onChange("subtitle", e.target.value)}
                                        data-testid="admin-field-subtitle"
                                        className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[14.5px] focus:border-[#0A0A0A] focus:outline-none"
                                    />
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Slug (auto from title if empty)">
                                        <input
                                            type="text"
                                            value={draft.slug || ""}
                                            onChange={(e) => onChange("slug", e.target.value)}
                                            data-testid="admin-field-slug"
                                            className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 font-mono text-[12.5px] focus:border-[#0A0A0A] focus:outline-none"
                                        />
                                    </Field>
                                    <Field label="Country">
                                        <select
                                            value={draft.country_code || "IN"}
                                            onChange={(e) => onChange("country_code", e.target.value)}
                                            data-testid="admin-field-country"
                                            className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[14.5px] focus:border-[#0A0A0A] focus:outline-none"
                                        >
                                            {COUNTRY_OPTIONS.map((c) => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>
                                <Field label="Cover image URL">
                                    <input
                                        type="text"
                                        value={draft.cover_image_url || ""}
                                        onChange={(e) => onChange("cover_image_url", e.target.value)}
                                        data-testid="admin-field-cover"
                                        placeholder="https://images.unsplash.com/..."
                                        className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 font-mono text-[12px] focus:border-[#0A0A0A] focus:outline-none"
                                    />
                                </Field>
                                <Field label="Author name">
                                    <input
                                        type="text"
                                        value={draft.author_name || ""}
                                        onChange={(e) => onChange("author_name", e.target.value)}
                                        data-testid="admin-field-author"
                                        className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[14.5px] focus:border-[#0A0A0A] focus:outline-none"
                                    />
                                </Field>
                                <Field label="Tags (comma separated)">
                                    <input
                                        type="text"
                                        value={tagsString}
                                        onChange={(e) =>
                                            onChange("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))
                                        }
                                        data-testid="admin-field-tags"
                                        className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[14.5px] focus:border-[#0A0A0A] focus:outline-none"
                                    />
                                </Field>
                                <Field label="Body (Markdown)" required>
                                    <textarea
                                        value={draft.body_markdown || ""}
                                        onChange={(e) => onChange("body_markdown", e.target.value)}
                                        rows={18}
                                        data-testid="admin-field-body"
                                        placeholder="## Section&#10;&#10;Write the post in Markdown."
                                        className="h-[480px] w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 font-mono text-[13px] leading-relaxed focus:border-[#0A0A0A] focus:outline-none"
                                    />
                                </Field>
                                <details className="rounded-lg border border-[#E5E5E5] bg-white px-3 py-2">
                                    <summary className="cursor-pointer text-[12.5px] font-medium text-[#525252]">
                                        SEO overrides (optional)
                                    </summary>
                                    <div className="mt-3 grid gap-3">
                                        <Field label="SEO title">
                                            <input
                                                type="text"
                                                value={draft.seo?.title || ""}
                                                onChange={(e) => onSeoChange("title", e.target.value)}
                                                data-testid="admin-field-seo-title"
                                                className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[14px] focus:border-[#0A0A0A] focus:outline-none"
                                            />
                                        </Field>
                                        <Field label="SEO description">
                                            <textarea
                                                value={draft.seo?.description || ""}
                                                onChange={(e) => onSeoChange("description", e.target.value)}
                                                rows={2}
                                                data-testid="admin-field-seo-desc"
                                                className="w-full rounded-lg border border-[#E5E5E5] bg-white px-3 py-2 text-[13.5px] focus:border-[#0A0A0A] focus:outline-none"
                                            />
                                        </Field>
                                    </div>
                                </details>
                            </div>
                        )}

                        <div className="sticky bottom-0 -mx-6 mt-2 border-t border-[#E5E5E5] bg-white/95 px-6 py-4 backdrop-blur">
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => submit({ published: false })}
                                    disabled={saving || !draft.title}
                                    data-testid="admin-save-draft"
                                    className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-4 py-2 text-[13px] font-medium text-[#0A0A0A] transition hover:border-[#0A0A0A] disabled:opacity-50"
                                >
                                    <Save size={13} strokeWidth={2} /> Save draft
                                </button>
                                <button
                                    type="button"
                                    onClick={() => submit({ published: true })}
                                    disabled={saving || !draft.title || !draft.body_markdown}
                                    data-testid="admin-publish"
                                    className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] px-4 py-2 text-[13px] font-medium text-white transition hover:bg-[#1F1F1F] disabled:opacity-50"
                                >
                                    <Eye size={13} strokeWidth={2} /> Publish
                                </button>
                                {!isNew && draft.published && (
                                    <button
                                        type="button"
                                        onClick={() => submit({ published: false })}
                                        disabled={saving}
                                        data-testid="admin-unpublish"
                                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-4 py-2 text-[13px] font-medium text-[#525252] transition hover:border-[#0A0A0A] disabled:opacity-50"
                                    >
                                        <EyeOff size={13} strokeWidth={2} /> Unpublish
                                    </button>
                                )}
                                {!isNew && (
                                    <button
                                        type="button"
                                        onClick={remove}
                                        data-testid="admin-edit-delete"
                                        className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-[#E5E5E5] bg-white px-4 py-2 text-[13px] font-medium text-[#9C2A2A] transition hover:border-[#9C2A2A]"
                                    >
                                        <Trash2 size={13} strokeWidth={2} /> Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-[#E5E5E5] bg-white p-6">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3]">Live preview</div>
                        <h2 className="mt-3 text-[26px] font-semibold leading-tight tracking-tight">
                            {draft.title || "Post title preview"}
                        </h2>
                        {draft.subtitle && (
                            <p className="mt-2 text-[15px] leading-relaxed text-[#525252]">{draft.subtitle}</p>
                        )}
                        {draft.cover_image_url && (
                            <img
                                src={draft.cover_image_url}
                                alt=""
                                className="mt-5 max-h-[260px] w-full rounded-xl border border-[#E5E5E5] object-cover"
                            />
                        )}
                        <div
                            className="mt-6 text-[14.5px] leading-[1.7] text-[#0A0A0A] [&_h2]:mt-7 [&_h2]:text-[20px] [&_h2]:font-semibold [&_h2]:tracking-tight [&_h3]:mt-6 [&_h3]:text-[17px] [&_h3]:font-semibold [&_p]:mt-4 [&_ul]:mt-4 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mt-1 [&_a]:underline [&_img]:mt-6 [&_img]:rounded-lg [&_img]:border [&_img]:border-[#E5E5E5]"
                            data-testid="admin-edit-preview"
                        >
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {draft.body_markdown || "_Start writing the post body in markdown — preview renders here._"}
                            </ReactMarkdown>
                        </div>
                    </div>
                </div>
            </AdminShell>
        </Gate>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-[11.5px] font-medium uppercase tracking-wider text-[#737373]">
                {label}{required && <span className="ml-0.5 text-[#9C2A2A]">*</span>}
            </span>
            {children}
        </label>
    );
}


// ==========================================================================
// Sprint 3 — Share Reports
// ==========================================================================
const REPORT_STATUSES = ["open", "reviewed", "actioned"];

export function AdminShareReportsPage() {
    const { token } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("open");

    const reload = () => {
        if (!token) return;
        setLoading(true);
        const params = statusFilter === "all" ? {} : { status: statusFilter };
        axios
            .get(`${API}/admin/share_reports`, {
                headers: authHeaders(token),
                params,
            })
            .then((r) => setItems(r.data?.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(reload, [token, statusFilter]);

    const setStatus = async (id, status) => {
        await axios.patch(
            `${API}/admin/share_reports/${id}`,
            { status },
            { headers: authHeaders(token) },
        );
        reload();
    };

    return (
        <Gate>
            <AdminShell active="reports">
                <div className="mx-auto w-full max-w-[1200px] px-6 py-10" data-testid="admin-reports">
                    <div className="flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                                Admin · UGC safety
                            </div>
                            <h1 className="mt-2 text-3xl font-semibold tracking-tight">Share reports</h1>
                        </div>
                        <div className="flex gap-1.5" data-testid="reports-filter">
                            {["open", "reviewed", "actioned", "all"].map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setStatusFilter(s)}
                                    data-testid={`reports-filter-${s}`}
                                    className={`rounded-full px-3 py-1 text-[12px] font-medium uppercase tracking-wider transition ${
                                        statusFilter === s
                                            ? "bg-[#0A0A0A] text-white"
                                            : "border border-[#E5E5E5] bg-white text-[#525252] hover:border-[#0A0A0A]"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="mt-8 overflow-hidden rounded-2xl border border-[#E5E5E5]">
                        <table className="w-full text-left text-[13px]">
                            <thead className="border-b border-[#E5E5E5] bg-[#FAFAFA] text-[#737373]">
                                <tr>
                                    <th className="px-4 py-3 font-medium">Created</th>
                                    <th className="px-4 py-3 font-medium">Reason</th>
                                    <th className="px-4 py-3 font-medium">Reporter</th>
                                    <th className="px-4 py-3 font-medium">Session</th>
                                    <th className="px-4 py-3 font-medium">Status</th>
                                    <th className="px-4 py-3 font-medium">Set status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan={6} className="px-4 py-10 text-center text-[#A3A3A3]">Loading…</td></tr>
                                )}
                                {!loading && items.length === 0 && (
                                    <tr><td colSpan={6} className="px-4 py-10 text-center text-[#A3A3A3]">No reports.</td></tr>
                                )}
                                {items.map((r) => (
                                    <tr key={r.id} className="border-b border-[#E5E5E5] last:border-b-0 align-top" data-testid={`report-row-${r.id}`}>
                                        <td className="px-4 py-3 text-[#737373]">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-[#0A0A0A]">{r.reason}</div>
                                            {r.details && (
                                                <div className="mt-1 text-[12px] text-[#525252]">{r.details}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-[#525252]">
                                            {r.reporter_user_id || "anonymous"}
                                            <div className="text-[11px] text-[#A3A3A3]">{r.reporter_ip || ""}</div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-[11px] text-[#525252]">{(r.session_id || "").slice(0, 8)}</td>
                                        <td className="px-4 py-3">
                                            <span className="rounded-full bg-[#F5F5F5] px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-[#525252]">
                                                {r.status || "open"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={r.status || "open"}
                                                onChange={(e) => setStatus(r.id, e.target.value)}
                                                data-testid={`report-status-${r.id}`}
                                                className="rounded-md border border-[#E5E5E5] bg-white px-2 py-1 text-[12px] focus:border-[#0A0A0A] focus:outline-none"
                                            >
                                                {REPORT_STATUSES.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </AdminShell>
        </Gate>
    );
}

// ==========================================================================
// Sprint 3 — Feature Flags
// ==========================================================================
const FLAG_LABELS = {
    ads_enabled: {
        title: "Sponsored ads",
        description:
            "When ON, an ad card is inserted between every 4 result cards in /api/chat responses.",
    },
};

export function AdminFlagsPage() {
    const { token } = useAuth();
    const [flags, setFlags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    const reload = () => {
        if (!token) return;
        setLoading(true);
        axios
            .get(`${API}/admin/flags`, { headers: authHeaders(token) })
            .then((r) => setFlags(r.data?.items || []))
            .catch(() => setFlags([]))
            .finally(() => setLoading(false));
    };
    useEffect(reload, [token]);

    const toggle = async (flag) => {
        setBusy(flag.key);
        try {
            await axios.patch(
                `${API}/admin/flags/${flag.key}`,
                { value: !flag.value },
                { headers: authHeaders(token) },
            );
            reload();
        } finally {
            setBusy(null);
        }
    };

    return (
        <Gate>
            <AdminShell active="flags">
                <div className="mx-auto w-full max-w-[900px] px-6 py-10" data-testid="admin-flags">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-[#A3A3A3]">
                        Admin · Runtime
                    </div>
                    <h1 className="mt-2 text-3xl font-semibold tracking-tight">Feature flags</h1>
                    <p className="mt-3 max-w-xl text-[13.5px] text-[#737373]">
                        Toggle runtime features without redeploying. Changes take effect within 60
                        seconds (the backend caches flag values in memory).
                    </p>

                    <div className="mt-8 flex flex-col gap-3">
                        {loading && (
                            <div className="rounded-2xl border border-[#E5E5E5] bg-white p-6 text-[13px] text-[#A3A3A3]">
                                Loading…
                            </div>
                        )}
                        {!loading && flags.length === 0 && (
                            <div className="rounded-2xl border border-[#E5E5E5] bg-white p-6 text-[13px] text-[#A3A3A3]">
                                No flags configured.
                            </div>
                        )}
                        {flags.map((f) => {
                            const meta = FLAG_LABELS[f.key] || { title: f.key, description: "" };
                            return (
                                <div
                                    key={f.key}
                                    data-testid={`flag-row-${f.key}`}
                                    className="flex items-center justify-between gap-4 rounded-2xl border border-[#E5E5E5] bg-white p-5"
                                >
                                    <div>
                                        <div className="text-[15px] font-semibold tracking-tight text-[#0A0A0A]">
                                            {meta.title}
                                        </div>
                                        <div className="mt-0.5 font-mono text-[11px] text-[#A3A3A3]">{f.key}</div>
                                        {meta.description && (
                                            <p className="mt-2 max-w-md text-[12.5px] leading-relaxed text-[#525252]">
                                                {meta.description}
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => toggle(f)}
                                        disabled={busy === f.key}
                                        data-testid={`flag-toggle-${f.key}`}
                                        aria-pressed={Boolean(f.value)}
                                        className={`relative inline-flex h-7 w-12 items-center rounded-full border transition ${
                                            f.value
                                                ? "border-[#0A0A0A] bg-[#0A0A0A]"
                                                : "border-[#E5E5E5] bg-white"
                                        } ${busy === f.key ? "opacity-60" : ""}`}
                                    >
                                        <span
                                            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                                                f.value ? "translate-x-6" : "translate-x-1"
                                            }`}
                                            style={f.value ? { backgroundColor: "#fff" } : { backgroundColor: "#0A0A0A" }}
                                        />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </AdminShell>
        </Gate>
    );
}
